const VIDEO_PATH = '/assets/video/observer-deluxe.mp4';
const FADE_OUT_MS = 480;
const LOAD_TIMEOUT_MS = 20_000;

/** Keeps the observer presentation pending until its movie has faded away. */
export class ObserverVideoOverlay {
  private readonly dialog = document.createElement('dialog');
  private readonly video = document.createElement('video');
  private readonly status = document.createElement('p');
  private readonly playButton = document.createElement('button');
  private readonly skipButton = document.createElement('button');
  private loadTimer: ReturnType<typeof setTimeout> | undefined;
  private fadeTimer: ReturnType<typeof setTimeout> | undefined;
  private disposed = false;
  private closing = false;
  private resumeWhenVisible = false;
  private previousFocus: HTMLElement | null = null;

  public constructor(
    private readonly onFinish: (outcome: 'success' | 'failure') => void,
    private readonly onVideoChange: (video: HTMLVideoElement | null) => void,
  ) {
    this.dialog.className = 'observer-video';
    this.dialog.setAttribute('aria-label', '盤理の観測者への到達');
    const frame = document.createElement('div');
    frame.className = 'observer-video__frame';
    this.video.className = 'observer-video__movie';
    this.video.setAttribute('aria-label', '盤理の観測者の記念映像');
    this.video.playsInline = true;
    this.video.preload = 'auto';
    this.video.loop = false;
    this.video.poster = '/assets/characters/board-logic-observer.webp';
    this.status.className = 'observer-video__status';
    this.status.setAttribute('role', 'status');
    this.status.textContent = '映像を読み込み中…';
    this.playButton.type = 'button';
    this.playButton.className = 'observer-video__play';
    this.playButton.textContent = '映像を再生';
    this.playButton.hidden = true;
    this.skipButton.type = 'button';
    this.skipButton.className = 'observer-video__skip';
    this.skipButton.textContent = 'スキップ';
    frame.append(this.video, this.status, this.playButton);
    this.dialog.append(frame, this.skipButton);

    this.video.addEventListener('playing', this.onPlaying);
    this.video.addEventListener('ended', this.onEnded);
    this.video.addEventListener('error', this.onError);
    this.video.addEventListener('waiting', this.onWaiting);
    this.playButton.addEventListener('click', this.onPlayClick);
    this.skipButton.addEventListener('click', this.onEnded);
    this.dialog.addEventListener('cancel', this.onCancel);
  }

  public play(): void {
    this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.append(this.dialog);
    this.dialog.showModal();
    this.skipButton.focus({ preventScroll: true });
    document.addEventListener('visibilitychange', this.onVisibility);
    this.onVideoChange(this.video);
    this.video.src = VIDEO_PATH;
    this.armLoadTimeout();
    void this.requestPlayback();
  }

  public destroy(): void {
    if (this.disposed) return;
    this.disposed = true;
    clearTimeout(this.loadTimer);
    clearTimeout(this.fadeTimer);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.video.removeEventListener('playing', this.onPlaying);
    this.video.removeEventListener('ended', this.onEnded);
    this.video.removeEventListener('error', this.onError);
    this.video.removeEventListener('waiting', this.onWaiting);
    this.playButton.removeEventListener('click', this.onPlayClick);
    this.skipButton.removeEventListener('click', this.onEnded);
    this.dialog.removeEventListener('cancel', this.onCancel);
    this.video.pause();
    this.video.removeAttribute('src');
    this.video.load();
    this.onVideoChange(null);
    this.dialog.close();
    this.dialog.remove();
    if (this.previousFocus?.isConnected && !this.previousFocus.inert) this.previousFocus.focus({ preventScroll: true });
  }

  private async requestPlayback(): Promise<void> {
    try {
      await this.video.play();
    } catch (error) {
      if (this.disposed || this.closing) return;
      if (error instanceof Error && error.name === 'AbortError' && (document.hidden || this.resumeWhenVisible)) return;
      clearTimeout(this.loadTimer);
      if (error instanceof Error && error.name === 'NotAllowedError') {
        this.status.hidden = true;
        this.playButton.hidden = false;
        this.playButton.focus({ preventScroll: true });
      } else {
        this.finish('failure');
      }
    }
  }

  private readonly onPlaying = (): void => {
    if (this.disposed || this.closing) return;
    clearTimeout(this.loadTimer);
    this.status.hidden = true;
    this.playButton.hidden = true;
    if (document.hidden) {
      this.resumeWhenVisible = true;
      this.video.pause();
    }
  };

  private readonly onPlayClick = (): void => {
    if (this.closing || this.disposed) return;
    this.playButton.hidden = true;
    this.skipButton.focus({ preventScroll: true });
    this.armLoadTimeout();
    void this.requestPlayback();
  };

  private readonly onEnded = (): void => this.finish('success');
  private readonly onError = (): void => this.finish('failure');
  private readonly onCancel = (event: Event): void => { event.preventDefault(); this.finish('success'); };
  private readonly onWaiting = (): void => {
    if (!this.closing && !this.disposed && !document.hidden) this.armLoadTimeout();
  };

  private readonly onVisibility = (): void => {
    if (this.closing || this.disposed) return;
    if (document.hidden) {
      clearTimeout(this.loadTimer);
      this.resumeWhenVisible = !this.video.paused && !this.video.ended;
      this.video.pause();
    } else if (this.resumeWhenVisible) {
      this.resumeWhenVisible = false;
      this.armLoadTimeout();
      void this.requestPlayback();
    }
  };

  private armLoadTimeout(): void {
    clearTimeout(this.loadTimer);
    this.loadTimer = setTimeout(() => this.finish('failure'), LOAD_TIMEOUT_MS);
  }

  private finish(outcome: 'success' | 'failure'): void {
    if (this.closing || this.disposed) return;
    this.closing = true;
    clearTimeout(this.loadTimer);
    this.video.pause();
    this.dialog.classList.add('observer-video--leaving');
    this.fadeTimer = setTimeout(() => {
      this.destroy();
      this.onFinish(outcome);
    }, FADE_OUT_MS);
  }
}
